// ============================================
// useChat.js - Chat State Management Hook
// ============================================
// React hook for managing chat sessions and messages
// Syncs with backend API

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  loadSessions,
  loadSession,
  sendMessage as apiSendMessage,
  deleteSession as apiDeleteSession,
  updateSession as apiUpdateSession,
  getUsageStatus,
} from '../services/chatService';

/**
 * Chat management hook
 * @param {Function} onUsageUpdate - Callback when usage changes
 * @returns {Object} Chat state and methods
 */
export function useChat(onUsageUpdate) {
  // ==========================================
  // STATE
  // ==========================================
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Track if we've loaded sessions
  const sessionsLoaded = useRef(false);

  // ==========================================
  // LOAD SESSIONS ON MOUNT
  // ==========================================
  useEffect(() => {
    const init = async () => {
      if (sessionsLoaded.current) return;
      sessionsLoaded.current = true;

      setInitialLoading(true);
      const { sessions: loadedSessions, error } = await loadSessions();

      if (error) {
        console.warn('Failed to load sessions:', error);
      } else {
        setSessions(loadedSessions);
      }

      setInitialLoading(false);
    };

    init();
  }, []);

  // ==========================================
  // SESSION HANDLERS
  // ==========================================

  /**
   * Refresh sessions list from backend
   */
  const refreshSessions = useCallback(async () => {
    const { sessions: loadedSessions } = await loadSessions();
    setSessions(loadedSessions);
  }, []);

  /**
   * Select a session and load its messages
   */
  const selectSession = useCallback(async (session) => {
    if (!session?.id) return;

    // If it's a temp session (not yet on backend), just set it
    if (String(session.id).startsWith('temp-')) {
      setActiveSession(session);
      setMessages([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    const { session: loadedSession, messages: loadedMessages, error } = 
      await loadSession(session.id);

    if (error) {
      setError(error);
      setIsLoading(false);
      return;
    }

    setActiveSession(loadedSession || session);
    setMessages(loadedMessages);
    setIsLoading(false);
  }, []);

  /**
   * Create a new session (local only until first message)
   */
  const newSession = useCallback(() => {
    const tempSession = {
      id: `temp-${Date.now()}`,
      title: 'New conversation',
      preview: 'Start a new conversation...',
      messages: [],
      isPinned: false,
      isArchived: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Add to top of sessions list
    setSessions((prev) => [tempSession, ...prev]);
    setActiveSession(tempSession);
    setMessages([]);
    setError(null);

    return tempSession;
  }, []);

  /**
   * Delete a session
   */
  const deleteSession = useCallback(async (sessionId) => {
    // Remove from local state immediately
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));

    // Clear if it was active
    if (activeSession?.id === sessionId) {
      setActiveSession(null);
      setMessages([]);
    }

    // Delete from backend (if not a temp session)
    if (!String(sessionId).startsWith('temp-')) {
      const { error } = await apiDeleteSession(sessionId);
      if (error) {
        console.warn('Failed to delete session:', error);
      }
    }
  }, [activeSession]);

  /**
   * Pin/unpin a session
   */
  const pinSession = useCallback((sessionId) => {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId ? { ...s, isPinned: !s.isPinned } : s
      )
    );
    // TODO: Sync with backend if you add pin support
  }, []);

  /**
   * Archive a session
   */
  const archiveSession = useCallback(async (sessionId) => {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId ? { ...s, isArchived: true } : s
      )
    );

    if (activeSession?.id === sessionId) {
      setActiveSession(null);
      setMessages([]);
    }

    // Update backend
    if (!String(sessionId).startsWith('temp-')) {
      await apiUpdateSession(sessionId, { is_active: false });
    }
  }, [activeSession]);

  /**
   * Rename a session
   */
  const renameSession = useCallback(async (sessionId, newTitle) => {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId ? { ...s, title: newTitle } : s
      )
    );

    if (activeSession?.id === sessionId) {
      setActiveSession((prev) => ({ ...prev, title: newTitle }));
    }

    // Update backend
    if (!String(sessionId).startsWith('temp-')) {
      await apiUpdateSession(sessionId, { title: newTitle });
    }
  }, [activeSession]);

  // ==========================================
  // MESSAGE HANDLERS
  // ==========================================

  /**
   * Send a message
   */
  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isSending) return;

    setIsSending(true);
    setError(null);

    // Determine session ID (null for new sessions)
    let sessionId = activeSession?.id;
    if (String(sessionId).startsWith('temp-')) {
      sessionId = null;
    }

    // Optimistically add user message
    const tempUserMsg = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    // Send to backend
    const result = await apiSendMessage(content, sessionId);

    if (result.error) {
      setError(result.error);
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
      setIsSending(false);
      return { error: result.error };
    }

    // Replace temp message with real ones
    setMessages((prev) => {
      const filtered = prev.filter((m) => m.id !== tempUserMsg.id);
      return [...filtered, result.userMessage, result.assistantMessage];
    });

    // Update session if it was a new one
    if (!sessionId || String(activeSession?.id).startsWith('temp-')) {
      const newSessionData = {
        id: result.sessionId,
        title: content.slice(0, 30) + (content.length > 30 ? '...' : ''),
        preview: content.slice(0, 50),
        isPinned: false,
        isArchived: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Update sessions list (replace temp with real)
      setSessions((prev) => {
        const filtered = prev.filter(
          (s) => !String(s.id).startsWith('temp-')
        );
        return [newSessionData, ...filtered];
      });

      setActiveSession(newSessionData);
    } else {
      // Update existing session in list
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                preview: content.slice(0, 50),
                updatedAt: new Date().toISOString(),
              }
            : s
        )
      );
    }

    // Notify about usage update
    if (onUsageUpdate) {
      onUsageUpdate({
        remaining: result.remaining,
        dailyLimit: result.dailyLimit,
      });
    }

    setIsSending(false);
    return { success: true };
  }, [activeSession, isSending, onUsageUpdate]);

  // ==========================================
  // RETURN
  // ==========================================

  return {
    // State
    sessions,
    activeSession,
    messages,
    isLoading,
    isSending,
    error,
    initialLoading,

    // Session methods
    refreshSessions,
    selectSession,
    newSession,
    deleteSession,
    pinSession,
    archiveSession,
    renameSession,

    // Message methods
    sendMessage,

    // Utilities
    clearError: () => setError(null),
    setActiveSession,
  };
}

export default useChat;
